import React, { useState } from 'react';
import { Plus, Search, MessageSquare, Folder, ChevronDown, ChevronRight, LayoutDashboard, Zap, Repeat, GraduationCap, Ticket, Sparkles, ChevronsUpDown, MoreHorizontal } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { ScrollArea } from './ScrollArea';
import { Avatar, AvatarFallback } from './Avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './Collapsible';
import { Chat, Project } from '../types';
interface SidebarProps {
  chats: Chat[];
  projects: Project[];
  activeChatId: string | null;
  onSelectChat: (id: string) => void;
  onNewChat: () => void;
}
export function Sidebar({
  chats,
  projects,
  activeChatId,
  onSelectChat,
  onNewChat
}: SidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [openProjects, setOpenProjects] = useState<Record<string, boolean>>({});
  const toggleProject = (id: string) => {
    setOpenProjects(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const filteredChats = chats.filter(chat => chat.title.toLowerCase().includes(searchQuery.toLowerCase()));
  const mainChats = filteredChats.filter(chat => !chat.projectId);
  return <div className="w-[280px] h-full bg-[#1c2033] border-r border-white/5 flex flex-col text-sm text-slate-300 flex-shrink-0" data-id="element-78">
      {/* Header / Logo */}
      <div className="p-4 flex items-center justify-between" data-id="element-79">
        <div className="flex items-center gap-2 font-bold text-white tracking-wide" data-id="element-80">
          <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center text-[10px]" data-id="element-81">
            M
          </div>
          MARKETING SECRETS
        </div>
      </div>

      {/* Team Selector */}
      <div className="px-4 pb-3" data-id="element-82">
        <Button variant="ghost" className="w-full justify-between px-2 h-8 text-slate-300 hover:text-white hover:bg-white/5" data-id="element-83">
          <div className="flex items-center gap-2" data-id="element-84">
            <div className="w-4 h-4 rounded bg-[#2a2f42] flex items-center justify-center text-[10px]" data-id="element-85">
              t
            </div>
            <span data-id="element-86">test</span>
          </div>
          <ChevronsUpDown className="h-3 w-3 opacity-50" data-id="element-87" />
        </Button>
      </div>

      <ScrollArea className="flex-1 px-2" data-id="element-88">
        {/* Chief of Staff Section */}
        <div className="mb-4" data-id="element-89">
          <div className="px-2 mb-2 text-xs font-semibold text-[#6b7194] tracking-wider" data-id="element-90">
            CHIEF OF STAFF
          </div>

          <div className="space-y-0.5" data-id="element-91">
            <div className="px-2" data-id="element-92">
              <Button variant="ghost" className="w-full justify-between h-8 px-2 bg-[#252a42] text-slate-200 hover:bg-[#2d3250] font-normal" data-id="element-93">
                <div className="flex items-center" data-id="element-94">
                  <div className="w-5 h-5 rounded-full bg-[#6c5ce7] flex items-center justify-center text-[9px] font-bold text-white mr-2" data-id="element-95">
                    CS
                  </div>
                  Your Chief of Staff
                </div>
                <div className="flex items-center gap-1.5" data-id="element-96">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-97" />
                  <span className="text-[10px] text-emerald-500 font-medium" data-id="element-98">
                    Active
                  </span>
                  <ChevronRight className="h-3 w-3 text-slate-500 ml-1" data-id="element-99" />
                </div>
              </Button>
            </div>

            <div className="px-2" data-id="element-100">
              <Button onClick={onNewChat} variant="ghost" className="w-full justify-start h-8 px-2 gap-2 text-[#8b8fa8] hover:text-slate-200 hover:bg-white/5 font-normal" data-id="element-101">
                <MessageSquare className="h-4 w-4 opacity-70" data-id="element-102" />
                New Chat
              </Button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-2 mb-3" data-id="element-103">
          <div className="relative" data-id="element-104">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-[#6b7194]" data-id="element-105" />
            <Input placeholder="Search chats..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-8 pl-8 bg-black/20 border-transparent focus-visible:ring-1 focus-visible:ring-[#4a4f6a] text-xs placeholder:text-[#6b7194]" data-id="element-106" />
          </div>
        </div>

        {/* Chat List */}
        <div className="space-y-0.5 mb-4" data-id="element-107">
          {mainChats.map(chat => <ChatItem key={chat.id} chat={chat} isActive={activeChatId === chat.id} onClick={() => onSelectChat(chat.id)} data-id="element-108" />)}
        </div>

        {/* Projects Section */}
        {projects.length > 0 && <div className="mb-4" data-id="element-109">
            <div className="px-2 mb-1 flex items-center justify-between group" data-id="element-110">
              <span className="text-xs font-semibold text-[#6b7194] tracking-wider" data-id="element-111">
                PROJECTS
              </span>
              <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 text-[#6b7194] hover:text-white hover:bg-white/10" data-id="element-112">
                <Plus className="h-3.5 w-3.5" data-id="element-113" />
              </Button>
            </div>

            <div className="space-y-0.5" data-id="element-114">
              {projects.map(project => {
            const projectChats = filteredChats.filter(c => c.projectId === project.id);
            if (projectChats.length === 0 && searchQuery) return null;
            const isOpen = openProjects[project.id] ?? false;
            return <Collapsible key={project.id} open={isOpen} onOpenChange={() => toggleProject(project.id)} data-id="element-115">
                    <div className="px-2" data-id="element-116">
                      <CollapsibleTrigger asChild data-id="element-117">
                        <Button variant="ghost" className={`w-full justify-between h-8 px-2 font-normal ${isOpen ? 'bg-white/5 text-slate-200' : 'text-[#8b8fa8] hover:bg-white/5 hover:text-slate-200'}`} data-id="element-118">
                          <div className="flex items-center min-w-0" data-id="element-119">
                            {isOpen ? <ChevronDown className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-[#6b7194]" data-id="element-120" /> : <ChevronRight className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-[#6b7194]" data-id="element-121" />}
                            <Folder className="h-3.5 w-3.5 mr-2 flex-shrink-0 text-[#6c5ce7]" data-id="element-122" />
                            <span className="truncate" data-id="element-123">{project.name}</span>
                          </div>
                          <span className="text-[11px] text-[#6b7194] flex-shrink-0 ml-2" data-id="element-124">
                            {projectChats.length}
                          </span>
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="pl-5 space-y-0.5 mt-0.5" data-id="element-125">
                      {projectChats.map(chat => <ChatItem key={chat.id} chat={chat} isActive={activeChatId === chat.id} onClick={() => onSelectChat(chat.id)} data-id="element-126" />)}
                    </CollapsibleContent>
                  </Collapsible>;
          })}
            </div>
          </div>}

        {/* Workspace Section */}
        <div className="mb-6" data-id="element-127">
          <div className="px-2 mb-2 text-xs font-semibold text-[#6b7194] tracking-wider" data-id="element-128">
            WORKSPACE
          </div>
          <div className="space-y-0.5" data-id="element-129">
            <NavItem icon={<LayoutDashboard className="h-4 w-4" data-id="element-131" />} label="Dashboard" data-id="element-130" />
            <NavItem icon={<Sparkles className="h-4 w-4" data-id="element-133" />} label="Attractive Characters" data-id="element-132" />
            <NavItem icon={<Zap className="h-4 w-4" data-id="element-135" />} label="Sprints" data-id="element-134" />
            <NavItem icon={<Repeat className="h-4 w-4" data-id="element-137" />} label="Routines" data-id="element-136" />
            <NavItem icon={<GraduationCap className="h-4 w-4" data-id="element-139" />} label="Training & Courses" data-id="element-138" />
            <NavItem icon={<Ticket className="h-4 w-4" data-id="element-141" />} label="Backstage Pass" data-id="element-140" />
            <NavItem icon={<div className="h-4 w-4" data-id="element-143" />} label="Software" data-id="element-142" />
          </div>
        </div>
      </ScrollArea>

      {/* User Profile */}
      <div className="p-3 border-t border-white/5" data-id="element-144">
        <Button variant="ghost" className="w-full justify-start px-2 h-12 hover:bg-white/5" data-id="element-145">
          <Avatar className="h-8 w-8 mr-3 rounded-md bg-[#252a42]" data-id="element-146">
            <AvatarFallback className="bg-[#252a42] text-slate-300 rounded-md" data-id="element-147">
              J
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start text-left flex-1 overflow-hidden" data-id="element-148">
            <span className="text-sm font-medium text-slate-200 truncate w-full" data-id="element-149">
              jase
            </span>
            <span className="text-xs text-[#6b7194] truncate w-full" data-id="element-150">
              jase@clickfunnels.com
            </span>
          </div>
          <ChevronsUpDown className="h-4 w-4 text-[#6b7194]" data-id="element-151" />
        </Button>
      </div>
    </div>;
}
function ChatItem({
  chat,
  isActive,
  onClick
}: {
  chat: Chat;
  isActive: boolean;
  onClick: () => void;
}) {
  return <div className="group relative px-2" data-id="element-152">
      <Button variant="ghost" onClick={onClick} className={`w-full justify-start h-8 px-2 font-normal ${isActive ? 'bg-[#252a42] text-white' : 'text-[#8b8fa8] hover:bg-white/5 hover:text-slate-200'}`} data-id="element-153">
        <MessageSquare className={`h-3.5 w-3.5 mr-2 flex-shrink-0 ${isActive ? 'text-emerald-400' : 'opacity-70'}`} data-id="element-154" />
        <span className="truncate text-sm" data-id="element-155">{chat.title}</span>
      </Button>

      <div className={`absolute right-3 top-1/2 -translate-y-1/2 flex items-center ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} data-id="element-156">
        <div className={`h-8 w-8 absolute right-0 bg-gradient-to-l from-[#1c2033] to-transparent pointer-events-none ${isActive ? 'from-[#252a42]' : 'group-hover:from-[#1f2336]'}`} data-id="element-157" />
        <Button variant="ghost" size="icon" className="h-6 w-6 relative z-10 text-[#6b7194] hover:text-white hover:bg-[#2a2f42]" data-id="element-158">
          <MoreHorizontal className="h-3.5 w-3.5" data-id="element-159" />
        </Button>
      </div>
    </div>;
}
function NavItem({
  icon,
  label
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return <div className="px-2" data-id="element-160">
      <Button variant="ghost" className="w-full justify-start h-8 px-2 text-[#8b8fa8] hover:text-slate-200 hover:bg-white/5 font-normal" data-id="element-161">
        <span className="mr-2 opacity-70" data-id="element-162">{icon}</span>
        {label}
      </Button>
    </div>;
}